import React, { JSX, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  FaceSmileIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { useAuth } from 'context/user'
import { useEntitlements, planDisplayName } from 'context/plan'
import { useTwitchConnection } from 'hooks/useTwitchConnection'
import { AccountOverlayLinkState, useAccountOverlayLink } from 'hooks/useAccountOverlayLink'
import { useAccountYouTubeSessions, useEventYouTubeSessions } from 'hooks/useYouTubeSessions'
import {
  useYouTubeConnectionIdentity,
  YouTubeSessionWidget,
  YouTubeTermsFooter
} from 'components/YouTubeSessionWidget'
import { LoadingSpinner } from 'components/Loading'
import { NumberInput } from 'components/NumberInput'
import { ErrorInline } from 'components/ErrorInline'
import { SavedNote } from 'components/SavedNote'
import { ProviderBrandIcon, TWITCH_BRAND_ICON_SRC } from 'components/ProviderBrandIcon'
import { graphqlRequest } from 'lib/helpers'
import { sortByName } from 'lib/elementOrder'
import { EmoteWallMockup } from 'components/overlayPreview/OverlayMockups'
import { usePrefersReducedMotion } from 'components/overlayPreview/previewFeeds'
import { youtubeListOf } from 'lib/wallEmoji'
import { wallBlockedWordsHelpOf } from 'lib/wallBlockedWords'
import { DeletionResult, EmoteWallSummary, EventDetail } from 'types/api'
import {
  SectionCard,
  FieldLabel,
  BlockedWordsField,
  parseBlockedWords,
  OVERLAY_LINK_NOTE,
  OverlayLinkBlock,
  OverlayLinkRowActions,
  QuickOverlayManagerView,
  STREAM_ENDED_NOTE,
  StreamScope,
  reapplyStreamWrite,
  YouTubeChannelsBlock,
  YouTubeChannelsBlockCopy,
  ownUnlistedLogin
} from 'components/chatOverlays/ChatOverlayManager'
import { eventHasYouTubeSource } from 'components/EventYouTubeSources'
import { StreamWallYouTubeSources, knownStreamYouTubeChannels } from 'components/chatOverlays/StreamYouTubeSources'
import { CopyIntoStreamDialog } from 'components/chatOverlays/CopyIntoStreamDialog'
import createEmoteWallMutation from 'graphql/createEmoteWall.graphql'
import updateEmoteWallMutation from 'graphql/updateEmoteWall.graphql'
import deleteEmoteWallMutation from 'graphql/deleteEmoteWall.graphql'
import listMyEmoteWallsQuery from 'graphql/listMyEmoteWalls.graphql'
import duplicateEmoteWallMutation from 'graphql/duplicateEmoteWall.graphql'
import updateEmoteWallYouTubeChannelsMutation from 'graphql/updateEmoteWallYouTubeChannels.graphql'
import createEventEmoteWallMutation from 'graphql/createEventEmoteWall.graphql'
import updateEventEmoteWallMutation from 'graphql/updateEventEmoteWall.graphql'
import deleteEventEmoteWallMutation from 'graphql/deleteEventEmoteWall.graphql'

const FALLBACK_MAX_CHANNELS = 3
const MIN_EMOTE_SIZE = 16
const MAX_EMOTE_SIZE = 256
const MIN_DENSITY = 1
const MAX_DENSITY = 100
const MIN_DURATION_SECONDS = 2
const MAX_DURATION_SECONDS = 30

type EmoteWallStyle = 'RAIN' | 'POP'

const normalizeStyle = (raw: string): EmoteWallStyle =>
  raw === 'POP' ? 'POP' : 'RAIN'

const styleLabel = (raw: string): string =>
  normalizeStyle(raw) === 'POP' ? 'Pop' : 'Rain'

export type EmoteWallSource = 'TWITCH' | 'YOUTUBE'

export interface EmoteWallDraft {
  name: string
  twitchChatChannels: string[]
  style: EmoteWallStyle
  emoteSize: number
  density: number
  durationSeconds: number
  showTwitchEmotes: boolean
  show7tvEmotes: boolean
  showBttvEmotes: boolean
  showFfzEmotes: boolean
  youtubeChatChannels: string[]
  source: EmoteWallSource
  blockedWordsText: string
}

type NumericWallField = {
  [K in keyof EmoteWallDraft]: EmoteWallDraft[K] extends number ? K : never
}[keyof EmoteWallDraft]

type ProviderToggleField = {
  [K in keyof EmoteWallDraft]: EmoteWallDraft[K] extends boolean ? K : never
}[keyof EmoteWallDraft]

const emptyDraft = (): EmoteWallDraft => ({
  name: '',
  twitchChatChannels: [],
  style: 'RAIN',
  emoteSize: 56,
  density: 40,
  durationSeconds: 8,
  showTwitchEmotes: true,
  show7tvEmotes: true,
  showBttvEmotes: true,
  showFfzEmotes: true,
  youtubeChatChannels: [],
  source: 'TWITCH',
  blockedWordsText: ''
})

const savedYouTubeListOf = (wall: EmoteWallSummary): string[] => youtubeListOf(wall.youtubeChatChannels)

export const draftFromWall = (wall: EmoteWallSummary): EmoteWallDraft => {
  const youtube = savedYouTubeListOf(wall)
  return {
    name: wall.name,
    twitchChatChannels: [...wall.twitchChatChannels],
    style: normalizeStyle(wall.style),
    emoteSize: wall.emoteSize,
    density: wall.density,
    durationSeconds: wall.durationSeconds,
    showTwitchEmotes: wall.showTwitchEmotes,
    show7tvEmotes: wall.show7tvEmotes,
    showBttvEmotes: wall.showBttvEmotes,
    showFfzEmotes: wall.showFfzEmotes,
    youtubeChatChannels: youtube,
    source: youtube.length > 0 ? 'YOUTUBE' : 'TWITCH',
    blockedWordsText: youtubeListOf(wall.blockedWords).join('\n')
  }
}

export const wallSaveVariables = (draft: EmoteWallDraft): Record<string, unknown> => {
  const youtube = draft.source === 'YOUTUBE'
  return {
    name: draft.name.trim(),
    twitchChatChannels: youtube ? [] : draft.twitchChatChannels,
    youtubeChatChannels: youtube ? draft.youtubeChatChannels : [],
    style: draft.style,
    emoteSize: draft.emoteSize,
    density: draft.density,
    durationSeconds: draft.durationSeconds,
    showTwitchEmotes: draft.showTwitchEmotes,
    show7tvEmotes: draft.show7tvEmotes,
    showBttvEmotes: draft.showBttvEmotes,
    showFfzEmotes: draft.showFfzEmotes,
    blockedWords: parseBlockedWords(draft.blockedWordsText)
  }
}

export const wallPreviewConfigFromDraft = (draft: EmoteWallDraft): EmoteWallDraft & { blockedWords: string[] } =>
  ({ ...draft, blockedWords: parseBlockedWords(draft.blockedWordsText) })

export const wallDraftValid = (draft: EmoteWallDraft): boolean =>
  draft.name.trim() !== '' &&
  (draft.source === 'YOUTUBE' ? draft.youtubeChatChannels.length > 0 : draft.twitchChatChannels.length > 0)

const wallYouTubeCopy = (isEdit: boolean): YouTubeChannelsBlockCopy => ({
  target: 'this emote wall',
  tail: 'The wall shows only the emoji from these chats.',
  unsavedNote: isEdit
    ? 'Saved with the wall when you press Save changes.'
    : 'Saved with the wall when you press Create emote wall.'
})

const WALL_SESSION_IDLE_COPY = 'Start a YouTube chat session for your own channel here, or add any public channel by its stream link above. We read live chat through the official YouTube Data API, only while a session runs, and each session feeds the chat overlays and emote walls that list its channel.'

const WALL_SOURCE_TOGGLES: Array<{ source: EmoteWallSource, label: string, testId: string }> = [
  { source: 'TWITCH', label: 'Twitch', testId: 'wall-source-twitch' },
  { source: 'YOUTUBE', label: 'YouTube', testId: 'wall-source-youtube' }
]

const normalizeLogin = (raw: string): string =>
  raw.trim().toLowerCase().replace(/^#/, '')

const WALL_STYLE_TOGGLES: Array<{ style: EmoteWallStyle, label: string, testId: string }> = [
  { style: 'RAIN', label: 'Rain', testId: 'wall-style-rain' },
  { style: 'POP', label: 'Pop', testId: 'wall-style-pop' }
]

export const STREAM_WALL_COPY = Object.freeze({
  heading: 'Emote walls in this stream',
  newButton: 'New emote wall',
  copyButton: 'Add one I already made',
  empty: 'No emote walls in this stream yet.',
  note: 'Emote walls made here belong to this stream. They show in browser overlay through this stream\'s key when you place them in a scene, and they are deleted with the stream. For an emote wall with its own link, use Quick Overlays.',
  sideNote: 'Place this emote wall in a scene in the overlay editor. It shows in browser overlay through this stream\'s key, so there is no separate link to copy. Saved changes show the next time the source loads.',
  deleteConfirm: (name: string): string => `Delete emote wall "${name}"? It is removed from every scene in this stream.`
})

export const streamWallRowsOf = (event: Pick<EventDetail, 'emoteWalls'>): EmoteWallSummary[] =>
  sortByName(event.emoteWalls ?? [], (w) => w.name, (w) => w.emoteWallId)

export const withStreamWallRow = (event: EventDetail, wall: EmoteWallSummary): EventDetail => {
  const rows = event.emoteWalls ?? []
  const exists = rows.some((w) => w.emoteWallId === wall.emoteWallId)
  return { ...event, emoteWalls: exists ? rows.map((w) => (w.emoteWallId === wall.emoteWallId ? wall : w)) : [...rows, wall] }
}
export const withoutStreamWallRow = (event: EventDetail, emoteWallId: string): EventDetail =>
  ({ ...event, emoteWalls: (event.emoteWalls ?? []).filter((w) => w.emoteWallId !== emoteWallId) })

const EmoteWallForm = ({
  existing,
  auth0Token,
  overlayLink,
  onSaved,
  onCancel,
  backLabel,
  stream = null
}: {
  existing: EmoteWallSummary | null
  auth0Token: string | undefined
  overlayLink: AccountOverlayLinkState | null
  onSaved: (wall: EmoteWallSummary) => void
  onCancel: () => void
  backLabel: string
  stream?: { event: EventDetail } | null
}): JSX.Element => {
  const isEdit = existing !== null
  const streamEvent = stream == null ? null : stream.event
  const streamEnded = streamEvent != null && streamEvent.status === 'ENDED'
  const [draft, setDraft] = useState<EmoteWallDraft>(
    existing === null ? emptyDraft() : draftFromWall(existing)
  )
  const [channelInput, setChannelInput] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState(false)
  useEffect(() => {
    setSavedNote(false)
  }, [draft])
  const previewReducedMotion = usePrefersReducedMotion(true)

  const { status: planStatus, limitFor, upgradeForLimit, account, plans } = useEntitlements()
  const channelCap = planStatus === 'ready'
    ? limitFor('twitchChannelsPerOverlay')
    : FALLBACK_MAX_CHANNELS
  const atChannelCap = channelCap !== null && draft.twitchChatChannels.length >= channelCap
  const channelUpgrade = atChannelCap
    ? upgradeForLimit('twitchChannelsPerOverlay', channelCap)
    : null

  const { login: myTwitchLogin } = useTwitchConnection()
  const quickAddLogin = atChannelCap
    ? null
    : ownUnlistedLogin(myTwitchLogin, draft.twitchChatChannels)

  const youtubeInvited = account?.youtubeChatInvited === true
  const savedYouTube = existing == null ? [] : savedYouTubeListOf(existing)
  const youtubeChoiceShown = youtubeInvited || savedYouTube.length > 0 || draft.source === 'YOUTUBE' ||
    (streamEvent != null && eventHasYouTubeSource(streamEvent))
  const {
    connected: youtubeConnected,
    channelId: ownYouTubeChannelId,
    channelTitle: ownYouTubeChannelTitle
  } = useYouTubeConnectionIdentity()
  const youtubeListCap = planStatus === 'ready'
    ? (limitFor('youtubeChannelsPerOverlay') ?? limitFor('twitchChannelsPerOverlay'))
    : FALLBACK_MAX_CHANNELS
  const atYouTubeChannelCap = youtubeListCap != null && draft.youtubeChatChannels.length >= youtubeListCap
  const youtubeChannelUpgrade = atYouTubeChannelCap && youtubeListCap != null
    ? plans.find((p) => {
      if (!p.purchasable) return false
      const rungCap = p.limits.youtubeChannelsPerOverlay ?? p.limits.twitchChannelsPerOverlay
      return rungCap == null || rungCap > youtubeListCap
    }) ?? null
    : null
  const youtubeLiveCap = account?.youtubeSessionsPerAccount ?? null
  const youtubeSessions = useAccountYouTubeSessions(streamEvent == null && youtubeChoiceShown && youtubeConnected)
  const streamSessions = useEventYouTubeSessions(
    streamEvent == null ? '' : streamEvent.eventId,
    streamEvent != null && draft.source === 'YOUTUBE'
  )
  const streamKnown = useMemo(() => streamEvent == null
    ? []
    : knownStreamYouTubeChannels({
      event: streamEvent,
      sessions: streamSessions.sessions,
      hostOwn: streamEvent.isHost === true ? { channelId: ownYouTubeChannelId, channelTitle: ownYouTubeChannelTitle } : null
    }), [streamEvent, streamSessions.sessions, ownYouTubeChannelId, ownYouTubeChannelTitle])
  const [youtubeAddBusy, setYoutubeAddBusy] = useState(false)
  const addYouTubeChannel = (channelId: string): void => {
    setDraft((d) => {
      if (d.youtubeChatChannels.includes(channelId)) return d
      if (youtubeListCap != null && d.youtubeChatChannels.length >= youtubeListCap) return d
      return { ...d, youtubeChatChannels: [...d.youtubeChatChannels, channelId] }
    })
  }
  const removeYouTubeChannel = (channelId: string): void => {
    setDraft((d) => ({ ...d, youtubeChatChannels: d.youtubeChatChannels.filter((c) => c !== channelId) }))
  }
  const editingWallId = existing == null ? null : existing.emoteWallId
  const savedIsYouTube = savedYouTube.length > 0
  const persistYouTubeChannels = useMemo(() => {
    if (editingWallId == null || !savedIsYouTube || streamEvent != null) return null
    return async (youtubeChatChannels: string[]): Promise<void> => {
      if (auth0Token == null) return
      const { wall } = await graphqlRequest<{ wall: EmoteWallSummary }>(
        updateEmoteWallYouTubeChannelsMutation, { emoteWallId: editingWallId, youtubeChatChannels }, auth0Token
      )
      onSaved(wall)
    }
  }, [editingWallId, savedIsYouTube, streamEvent, auth0Token, onSaved])

  const removeChannel = (login: string): void => {
    setDraft((d) => ({
      ...d,
      twitchChatChannels: d.twitchChatChannels.filter((c) => c !== login)
    }))
  }

  const addChannels = (): void => {
    const fresh = channelInput
      .split(/[\s,]+/)
      .map(normalizeLogin)
      .filter((c) => c !== '' && /^[a-z0-9_]{1,25}$/.test(c))
    if (fresh.length === 0) return
    setDraft((d) => {
      const merged = [...new Set([...d.twitchChatChannels, ...fresh])]
      return {
        ...d,
        twitchChatChannels: channelCap === null ? merged : merged.slice(0, channelCap)
      }
    })
    setChannelInput('')
  }

  const commitInt = (field: NumericWallField) => (n: number): void => {
    setDraft((d) => ({ ...d, [field]: n }))
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (auth0Token == null || submitting || youtubeAddBusy) return
    setSubmitting(true)
    setError(null)
    try {
      const variables = wallSaveVariables(draft)
      let wall: EmoteWallSummary
      if (streamEvent != null) {
        const streamVariables = { ...variables, eventId: streamEvent.eventId }
        const resp = existing == null
          ? await graphqlRequest<{ wall: EmoteWallSummary }>(
            createEventEmoteWallMutation, streamVariables, auth0Token
          )
          : await graphqlRequest<{ wall: EmoteWallSummary }>(
            updateEventEmoteWallMutation, { emoteWallId: existing.emoteWallId, ...streamVariables }, auth0Token
          )
        wall = resp.wall
      } else {
        const resp = existing == null
          ? await graphqlRequest<{ wall: EmoteWallSummary }>(
            createEmoteWallMutation, variables, auth0Token
          )
          : await graphqlRequest<{ wall: EmoteWallSummary }>(
            updateEmoteWallMutation, { emoteWallId: existing.emoteWallId, ...variables }, auth0Token
          )
        wall = resp.wall
      }
      setSubmitting(false)
      setSavedNote(true)
      onSaved(wall)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save emote wall')
      setSubmitting(false)
    }
  }

  const draftValid = wallDraftValid(draft)
  const youtubeSource = draft.source === 'YOUTUBE'
  const previewConfig = useMemo(() => wallPreviewConfigFromDraft(draft), [draft])
  const inputClass = 'w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'

  const formTitle = isEdit ? 'Edit emote wall' : 'New emote wall'

  const providerToggles: Array<{ field: ProviderToggleField, label: string }> = [
    { field: 'showTwitchEmotes', label: 'Twitch emotes (native, from the message itself)' },
    { field: 'show7tvEmotes', label: '7TV emotes (global + each channel’s set)' },
    { field: 'showBttvEmotes', label: 'BTTV emotes (global + each channel’s set)' },
    { field: 'showFfzEmotes', label: 'FFZ emotes (global + each channel’s set)' }
  ]

  return (
    <form
      onSubmit={(e) => { void handleSubmit(e) }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault()
        }
      }}
      className='mt-2 grid grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-3'
    >
      <div className='space-y-1.5 lg:col-span-2'>
        <fieldset className='contents space-y-1.5'>
          <SectionCard
            title={formTitle}
            action={
              <button
                type='button'
                onClick={onCancel}
                className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
              >
                <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                {backLabel}
              </button>
            }
          >
            <label className='mb-4 block'>
              <FieldLabel>Name</FieldLabel>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder='Main emote wall, Hype wall, …'
                required
                maxLength={80}
                className={inputClass}
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                An identifier, not displayed on the overlay.
              </p>
            </label>

            {youtubeChoiceShown && (
              <div className='mb-4'>
                <FieldLabel>Source</FieldLabel>
                <div className='inline-flex rounded-md border border-white/10 p-0.5' role='group' aria-label='Source'>
                  {WALL_SOURCE_TOGGLES.map(({ source, label, testId }) => (
                    <button
                      key={source}
                      type='button'
                      onClick={() => setDraft((d) => (d.source === source ? d : { ...d, source }))}
                      aria-pressed={draft.source === source}
                      data-testid={testId}
                      className={draft.source === source
                        ? 'rounded bg-white/10 px-3 py-1 text-xs font-semibold text-dm-text-primary ring-1 ring-inset ring-white/20'
                        : 'rounded px-3 py-1 text-xs font-medium text-dm-text-secondary hover:text-dm-text-primary'}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className='mt-1 text-[11px] text-dm-text-accent'>
                  A wall shows emotes from one platform: Twitch channels, or
                  YouTube live chats.
                </p>
              </div>
            )}

            {youtubeSource && streamEvent != null && (
              <>
                <StreamWallYouTubeSources
                  known={streamKnown}
                  channels={draft.youtubeChatChannels}
                  onChannelsChange={(next) => setDraft((d) => ({ ...d, youtubeChatChannels: next }))}
                />
                <p className='mt-2 text-[11px] text-dm-text-accent' data-testid='wall-youtube-emoji-note'>
                  A YouTube wall shows normal emoji from chat: emoji people
                  type, and emoji codes that name a normal emoji. Only the emoji reach the wall,
                  never the words or names. A small YouTube icon appears in
                  the wall&apos;s corner with the first YouTube emoji.
                </p>
              </>
            )}

            {youtubeSource && streamEvent == null && (
              <>
                <YouTubeChannelsBlock
                  connected={youtubeConnected}
                  channels={draft.youtubeChatChannels}
                  savedChannels={savedIsYouTube ? savedYouTube : null}
                  persist={persistYouTubeChannels}
                  copy={wallYouTubeCopy(isEdit)}
                  onDraftAdd={addYouTubeChannel}
                  onRemove={removeYouTubeChannel}
                  cap={youtubeListCap}
                  liveCap={youtubeLiveCap}
                  upgrade={youtubeChannelUpgrade}
                  ownChannelId={ownYouTubeChannelId}
                  ownChannelTitle={ownYouTubeChannelTitle}
                  auth0Token={auth0Token}
                  store={youtubeSessions}
                  formBusy={submitting}
                  onAddBusyChange={setYoutubeAddBusy}
                />
                <p className='mt-2 text-[11px] text-dm-text-accent' data-testid='wall-youtube-emoji-note'>
                  A YouTube wall shows normal emoji from chat: emoji people
                  type, and emoji codes that name a normal emoji.
                </p>
                {youtubeConnected && (
                  <div className='mt-4'>
                    <YouTubeSessionWidget scope={{ kind: 'account' }} variant='card' sessionsStore={youtubeSessions} idleCopy={WALL_SESSION_IDLE_COPY} />
                  </div>
                )}
                <YouTubeTermsFooter />
              </>
            )}
            <div hidden={youtubeSource} data-testid='wall-twitch-channels'>
              <FieldLabel>
                <span className='flex items-center gap-1.5'>
                  <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                  Twitch channels
                </span>
              </FieldLabel>
              {quickAddLogin !== null && (
                <button
                  type='button'
                  onClick={() => {
                    setDraft((d) => ({
                      ...d,
                      twitchChatChannels: [...new Set([...d.twitchChatChannels, quickAddLogin])]
                    }))
                  }}
                  data-testid='own-channel-quick-add'
                  className='mb-2 inline-flex items-center gap-1.5 rounded-md border border-brand/20 bg-brand/5 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:bg-brand/10'
                >
                  <PlusIcon className='size-3.5 shrink-0 text-brand' aria-hidden='true' />
                  Add your channel:{' '}
                  <span className='font-semibold text-dm-text-primary'>{quickAddLogin}</span>
                </button>
              )}
              <div className='flex gap-1.5'>
                <input
                  type='text'
                  value={channelInput}
                  onChange={(e) => setChannelInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    addChannels()
                  }}
                  disabled={atChannelCap}
                  placeholder='any_channel, another_channel'
                  autoComplete='off'
                  className='min-w-0 flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60'
                />
                <button
                  type='button'
                  onClick={addChannels}
                  disabled={atChannelCap}
                  className='shrink-0 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                >
                  Add
                </button>
              </div>
              <ul className='mt-2 space-y-1.5'>
                {draft.twitchChatChannels.map((login) => (
                  <li key={login}>
                    <label className='flex cursor-pointer items-center gap-2.5 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-sm hover:bg-dm-bg-accent/50'>
                      <input
                        type='checkbox'
                        checked
                        onChange={() => removeChannel(login)}
                        className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                      />
                      <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                      <span className='text-dm-text-primary'>{login}</span>
                    </label>
                  </li>
                ))}
              </ul>
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                {channelCap === null
                  ? <>Unlimited channels per overlay ({draft.twitchChatChannels.length} added): comma or space separated, then Add.</>
                  : <>Up to {channelCap} channels per overlay ({draft.twitchChatChannels.length}/{channelCap}): comma or space separated, then Add.</>}
              </p>
              {channelUpgrade !== null && (
                <p className='mt-1 text-[11px] font-medium text-brand'>
                  {planDisplayName(channelUpgrade)} allows{' '}
                  {channelUpgrade.limits.twitchChannelsPerOverlay ?? 'unlimited'} channels per overlay.
                </p>
              )}
            </div>

            <BlockedWordsField
              value={draft.blockedWordsText}
              onChange={(next) => setDraft((d) => ({ ...d, blockedWordsText: next }))}
              help={wallBlockedWordsHelpOf(draft.source)}
              testId='wall-blocked-words-help'
            />
          </SectionCard>

          <SectionCard title='Style'>
            <label className='mb-4 block'>
              <FieldLabel>Animation style</FieldLabel>
              <select
                value={draft.style}
                onChange={(e) => setDraft((d) => ({ ...d, style: normalizeStyle(e.target.value) }))}
                className={inputClass}
              >
                <option value='RAIN'>Rain: emotes fall from the top</option>
                <option value='POP'>Pop: emotes appear and fade in place</option>
              </select>
            </label>

            <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
              <label className='block'>
                <FieldLabel>Emote size (px)</FieldLabel>
                <NumberInput
                  value={draft.emoteSize}
                  min={MIN_EMOTE_SIZE}
                  max={MAX_EMOTE_SIZE}
                  onCommit={commitInt('emoteSize')}
                  className={inputClass}
                />
              </label>
              <label className='block'>
                <FieldLabel>Max on screen</FieldLabel>
                <NumberInput
                  value={draft.density}
                  min={MIN_DENSITY}
                  max={MAX_DENSITY}
                  onCommit={commitInt('density')}
                  className={inputClass}
                />
              </label>
              <label className='block'>
                <FieldLabel>Lifetime (seconds)</FieldLabel>
                <NumberInput
                  value={draft.durationSeconds}
                  min={MIN_DURATION_SECONDS}
                  max={MAX_DURATION_SECONDS}
                  onCommit={commitInt('durationSeconds')}
                  className={inputClass}
                />
              </label>
            </div>
            <p className='mt-3 text-[11px] text-dm-text-accent'>
              Size is each emote's height; max-on-screen caps how many are
              airborne at once; lifetime is how long each one lives.
            </p>

            <div className='mt-4 space-y-2' hidden={youtubeSource} data-testid='wall-emote-sources'>
              <FieldLabel>Emote sources</FieldLabel>
              {providerToggles.map(({ field, label }) => (
                <label key={field} className='flex items-center gap-2 text-sm text-dm-text-primary'>
                  <input
                    type='checkbox'
                    checked={draft[field]}
                    onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.checked }))}
                    className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                  />
                  {label}
                </label>
              ))}
            </div>
          </SectionCard>
        </fieldset>
      </div>

      <div className='lg:col-span-1'>
        <div className='sticky top-4 space-y-3'>
          <div className='flex items-center gap-2'>
            <button
              type='submit'
              disabled={submitting || youtubeAddBusy || !draftValid || streamEnded}
              className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {submitting && <LoadingSpinner />}
              {isEdit ? 'Save changes' : 'Create emote wall'}
            </button>
            <button
              type='button'
              onClick={onCancel}
              disabled={submitting}
              className='inline-flex flex-1 items-center justify-center rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
            >
              Cancel
            </button>
          </div>
          {error !== null && <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>}
          {savedNote && <SavedNote testId='emote-wall-saved' />}
          {streamEnded && (
            <p className='text-[11px] text-dm-text-accent' data-testid='stream-ended-note'>{STREAM_ENDED_NOTE}</p>
          )}

          {streamEvent != null && (
            <p className='rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary' data-testid='stream-wall-side-note'>
              {STREAM_WALL_COPY.sideNote}
            </p>
          )}

          {streamEvent == null && isEdit && overlayLink != null && (
            <>
              <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
                Browser source URL
              </h4>
              <OverlayLinkBlock link={overlayLink} label='emote wall' />
            </>
          )}
          {streamEvent == null && !isEdit && (
            <p className='text-[11px] text-dm-text-accent'>
              The browser-source link appears here (and in the list) after the
              first save.
            </p>
          )}

          <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            Preview
          </h4>

          <div className='inline-flex rounded-md border border-white/10 p-0.5' role='group' aria-label='Animation style'>
            {WALL_STYLE_TOGGLES.map(({ style, label, testId }) => (
              <button
                key={style}
                type='button'
                onClick={() => setDraft((d) => (d.style === style ? d : { ...d, style }))}
                aria-pressed={draft.style === style}
                data-testid={testId}
                className={draft.style === style
                  ? 'rounded bg-white/10 px-3 py-1 text-xs font-semibold text-dm-text-primary ring-1 ring-inset ring-white/20'
                  : 'rounded px-3 py-1 text-xs font-medium text-dm-text-secondary hover:text-dm-text-primary'}
              >
                {label}
              </button>
            ))}
          </div>
          <div className='overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-zinc-700 via-zinc-800 to-zinc-600 font-sans'>
            <EmoteWallMockup config={previewConfig} />
          </div>
          {youtubeSource && (
            <p className='text-[11px] text-dm-text-accent'>
              Sample emoji at a 1920 x 1080 source. The live overlay uses emoji
              from the YouTube chats above.
            </p>
          )}
          {!youtubeSource && (
            <p className='text-[11px] text-dm-text-accent'>
              Sample emotes at a 1920 x 1080 source, from this wall's own emote
              sources. The live overlay uses emotes from chat in the selected
              channels.
            </p>
          )}
          {previewReducedMotion && (
            <p className='text-[11px] text-dm-text-accent' data-testid='wall-preview-reduced-motion'>
              Your system asks for reduced motion, so the sample emotes hold
              still here and Rain and Pop look alike. The live overlay still
              animates.
            </p>
          )}
        </div>
      </div>
    </form>
  )
}

const WallListRow = ({
  wall,
  onEdit,
  onDuplicate,
  onDelete,
  busyAction,
  atCap
}: {
  wall: EmoteWallSummary
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
  busyAction: null | 'delete' | 'duplicate'
  atCap: boolean
}): JSX.Element => {
  const busy = busyAction != null
  const overlayLink = useAccountOverlayLink('emotes', wall.emoteWallId)
  const youtubeRowCount = savedYouTubeListOf(wall).length
  return (
    <li className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-4 py-3'>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className='shrink-0 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
            Emote wall
          </span>
          <div className='flex min-w-0 flex-col leading-tight'>
            <span className='truncate text-sm font-medium text-dm-text-primary'>
              {wall.name}
            </span>
            <span className='truncate text-[11px] text-dm-text-accent'>
              {youtubeRowCount > 0
                ? <>YouTube · {youtubeRowCount} live chat{youtubeRowCount === 1 ? '' : 's'} · {styleLabel(wall.style)} · {wall.emoteSize}px</>
                : <>{wall.twitchChatChannels.length} channel{wall.twitchChatChannels.length === 1 ? '' : 's'} · {wall.twitchChatChannels.join(', ')} · {styleLabel(wall.style)} · {wall.emoteSize}px</>}
            </span>
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-1.5'>
          <OverlayLinkRowActions link={overlayLink} label={`${wall.name} emote wall`} />
          <button
            type='button'
            onClick={onEdit}
            title='Edit'
            disabled={busy}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            <PencilSquareIcon className='size-3.5' aria-hidden='true' />
            Edit
          </button>
          <button
            type='button'
            onClick={onDuplicate}
            title={atCap ? "Your account's emote-wall limit is reached" : 'Duplicate'}
            disabled={busy || atCap}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busyAction === 'duplicate' ? <LoadingSpinner /> : <DocumentDuplicateIcon className='size-3.5' aria-hidden='true' />}
            Duplicate
          </button>
          <button
            type='button'
            onClick={onDelete}
            disabled={busy}
            title='Delete'
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busyAction === 'delete' ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
            Delete
          </button>
        </div>
      </div>
    </li>
  )
}

export const EmoteWallManager = ({
  view,
  basePath,
  backLabel,
  scope
}: {
  view: QuickOverlayManagerView
  basePath: string
  backLabel: string
  scope?: StreamScope
}): JSX.Element => {
  if (scope != null) {
    return <StreamEmoteWallManager view={view} basePath={basePath} backLabel={backLabel} scope={scope} />
  }
  const { auth0Token } = useAuth()
  const navigate = useNavigate()

  const [walls, setWalls] = useState<EmoteWallSummary[] | null>(null)
  const [listBusy, setListBusy] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  const loadWalls = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined) return
    setListBusy(true)
    setListError(null)
    try {
      const { walls: fetched } = await graphqlRequest<{ walls: EmoteWallSummary[] }>(
        listMyEmoteWallsQuery, {}, auth0Token
      )
      setWalls(fetched)
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load emote walls')
    } finally {
      setListBusy(false)
    }
  }, [auth0Token])

  useEffect(() => {
    void loadWalls()
  }, [loadWalls])

  const { enforcedLimitFor, upgradeForLimit } = useEntitlements()
  const wallCap = enforcedLimitFor('emoteWallsPerAccount')
  const wallCount = walls === null ? 0 : walls.length
  const atWallCap = wallCap !== null && wallCount >= wallCap
  const wallUpgrade = atWallCap
    ? upgradeForLimit('emoteWallsPerAccount', wallCap)
    : null

  const [rowBusy, setRowBusy] = useState<{ id: string, action: 'delete' | 'duplicate' } | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const goList = (): void => { void navigate(basePath) }
  const goCreate = (): void => { void navigate(`${basePath}/new`) }
  const goEdit = (emoteWallId: string): void => { void navigate(`${basePath}/${emoteWallId}`) }

  const editingId = typeof view === 'object' ? view.edit : null
  const editing = useMemo<EmoteWallSummary | null>(() => {
    if (editingId === null || walls === null) return null
    return walls.find((w) => w.emoteWallId === editingId) ?? null
  }, [editingId, walls])
  const editLink = useAccountOverlayLink('emotes', editing === null ? null : editing.emoteWallId)

  const upsertWall = (wall: EmoteWallSummary): void => {
    setWalls((prev) => {
      if (prev === null) return [wall]
      const exists = prev.some((w) => w.emoteWallId === wall.emoteWallId)
      return exists
        ? prev.map((w) => w.emoteWallId === wall.emoteWallId ? wall : w)
        : [...prev, wall]
    })
  }

  const removeWall = (emoteWallId: string): void => {
    setWalls((prev) => prev === null ? prev : prev.filter((w) => w.emoteWallId !== emoteWallId))
  }

  const handleDelete = async (wall: EmoteWallSummary): Promise<void> => {
    if (auth0Token == null || rowBusy != null) return
    const ok = globalThis.confirm(
      `Delete emote wall "${wall.name}"? Any browser source still pointing at its URL goes permanently blank.`
    )
    if (!ok) return
    setRowBusy({ id: wall.emoteWallId, action: 'delete' })
    setRowError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        deleteEmoteWallMutation, { emoteWallId: wall.emoteWallId }, auth0Token
      )
      if (result.deleted) removeWall(wall.emoteWallId)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete emote wall')
    } finally {
      setRowBusy(null)
    }
  }

  const handleDuplicate = async (wall: EmoteWallSummary): Promise<void> => {
    if (auth0Token == null || rowBusy != null) return
    setRowBusy({ id: wall.emoteWallId, action: 'duplicate' })
    setRowError(null)
    try {
      const { wall: created } = await graphqlRequest<{ wall: EmoteWallSummary | null }>(
        duplicateEmoteWallMutation, { emoteWallId: wall.emoteWallId }, auth0Token
      )
      if (created == null) {
        setRowError('Failed to duplicate emote wall')
        return
      }
      upsertWall(created)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to duplicate emote wall')
    } finally {
      setRowBusy(null)
    }
  }

  if (view === 'create') {
    return (
      <EmoteWallForm
        existing={null}
        auth0Token={auth0Token}
        overlayLink={editLink}
        onSaved={(wall) => {
          upsertWall(wall)
          void navigate(`${basePath}/${wall.emoteWallId}`, { replace: true })
        }}
        onCancel={goList}
        backLabel={backLabel}
      />
    )
  }

  if (typeof view === 'object') {
    if (walls === null) {
      return (
        <div className='mt-4 flex items-center justify-center px-4 pb-4 py-10'>
          <LoadingSpinner />
        </div>
      )
    }
    if (editing === null) {
      return (
        <div className='mt-4 px-4 pb-4'>
          <ErrorInline>That emote wall no longer exists.</ErrorInline>
          <button onClick={goList} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
            {backLabel}
          </button>
        </div>
      )
    }
    return (
      <EmoteWallForm
        existing={editing}
        auth0Token={auth0Token}
        overlayLink={editLink}
        onSaved={upsertWall}
        onCancel={goList}
        backLabel={backLabel}
      />
    )
  }

  let listContent: JSX.Element
  if (walls === null) {
    listContent = (
      <div className='flex items-center justify-center rounded-lg border border-dashed border-white/10 px-4 py-10'>
        <LoadingSpinner />
      </div>
    )
  } else if (walls.length === 0) {
    listContent = (
      <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
        <FaceSmileIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
        <p>No emote walls yet.</p>
        <button
          type='button'
          onClick={goCreate}
          className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
        >
          <PlusIcon className='size-3.5' aria-hidden='true' />
          Create the first one
        </button>
      </div>
    )
  } else {
    listContent = (
      <ul className='space-y-2' data-testid='emote-walls-list'>
        {sortByName(walls, (w) => w.name, (w) => w.emoteWallId).map((wall) => (
          <WallListRow
            key={wall.emoteWallId}
            wall={wall}
            onEdit={() => goEdit(wall.emoteWallId)}
            onDuplicate={() => { void handleDuplicate(wall) }}
            onDelete={() => { void handleDelete(wall) }}
            busyAction={rowBusy?.id === wall.emoteWallId ? rowBusy.action : null}
            atCap={atWallCap}
          />
        ))}
      </ul>
    )
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <SectionCard
        title='Emote Walls'
        action={
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => { void loadWalls() }}
              disabled={listBusy}
              title='Refresh'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {listBusy ? <LoadingSpinner /> : <ArrowPathIcon className='size-3.5' aria-hidden='true' />}
              Refresh
            </button>
            <button
              type='button'
              data-testid='add-emote-wall-button'
              onClick={goCreate}
              disabled={atWallCap}
              title={atWallCap ? "Your account's emote-wall limit is reached" : undefined}
              className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              <PlusIcon className='size-3.5' aria-hidden='true' />
              Add new emote wall
            </button>
          </div>
        }
      >
        {listError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setListError(null)}>{listError}</ErrorInline>
          </div>
        )}
        {rowError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}

        {atWallCap && (
          <div className='mb-3 rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 text-[11px] text-dm-text-secondary'>
            Your account has reached its emote-wall limit ({wallCap}).
            {wallUpgrade !== null && (
              <> <span className='font-medium text-brand'>{planDisplayName(wallUpgrade)} allows {wallUpgrade.limits.emoteWallsPerAccount ?? 'unlimited'} per account.</span></>
            )}
          </div>
        )}

        {listContent}

        <div className='mt-4 space-y-1 text-[11px] text-dm-text-accent'>
          <p>
            Each emote wall has its own browser-source URL. Add it to your streaming software with a transparent background.
          </p>
          <p>{OVERLAY_LINK_NOTE}</p>
        </div>
      </SectionCard>
    </div>
  )
}

const StreamWallListRow = ({
  wall,
  onEdit,
  onDelete,
  deleting,
  busy
}: {
  wall: EmoteWallSummary
  onEdit: () => void
  onDelete: () => void
  deleting: boolean
  busy: boolean
}): JSX.Element => {
  const youtubeRowCount = savedYouTubeListOf(wall).length
  return (
    <li className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-4 py-3' data-testid='stream-wall-row'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className='shrink-0 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
            Emote wall
          </span>
          <div className='flex min-w-0 flex-col leading-tight'>
            <span className='truncate text-sm font-medium text-dm-text-primary'>
              {wall.name}
            </span>
            <span className='truncate text-[11px] text-dm-text-accent'>
              {youtubeRowCount > 0
                ? <>YouTube · {youtubeRowCount} live chat{youtubeRowCount === 1 ? '' : 's'} · {styleLabel(wall.style)} · {wall.emoteSize}px</>
                : <>{wall.twitchChatChannels.length} channel{wall.twitchChatChannels.length === 1 ? '' : 's'} · {wall.twitchChatChannels.join(', ')} · {styleLabel(wall.style)} · {wall.emoteSize}px</>}
            </span>
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-1.5'>
          <button
            type='button'
            onClick={onEdit}
            title='Edit'
            disabled={busy}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            <PencilSquareIcon className='size-3.5' aria-hidden='true' />
            Edit
          </button>
          <button
            type='button'
            onClick={onDelete}
            disabled={busy}
            title='Delete'
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {deleting ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
            Delete
          </button>
        </div>
      </div>
    </li>
  )
}

export const StreamEmoteWallManager = ({
  view,
  basePath,
  backLabel,
  scope
}: {
  view: QuickOverlayManagerView
  basePath: string
  backLabel: string
  scope: StreamScope
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const navigate = useNavigate()
  const { event, refetchEvent, setEvent } = scope
  const ended = event.status === 'ENDED'
  const rows = useMemo(() => streamWallRowsOf(event), [event])
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)
  const [copyOpen, setCopyOpen] = useState(false)

  const goList = (): void => { void navigate(basePath) }
  const goCreate = (): void => { void navigate(`${basePath}/new`) }
  const goEdit = (emoteWallId: string): void => { void navigate(`${basePath}/${emoteWallId}`) }

  const refresh = (fold: (prev: EventDetail) => EventDetail): void => {
    void refetchEvent()
      .then(() => { reapplyStreamWrite(setEvent, fold) })
      .catch(() => {

      })
  }
  const upsert = (wall: EmoteWallSummary): void => {
    const fold = (prev: EventDetail): EventDetail => withStreamWallRow(prev, wall)
    reapplyStreamWrite(setEvent, fold)
    refresh(fold)
  }

  const handleDelete = async (wall: EmoteWallSummary): Promise<void> => {
    if (auth0Token == null || deletingId != null) return
    if (!globalThis.confirm(STREAM_WALL_COPY.deleteConfirm(wall.name))) return
    setDeletingId(wall.emoteWallId)
    setRowError(null)
    try {
      await graphqlRequest<{ result: DeletionResult }>(
        deleteEventEmoteWallMutation, { eventId: event.eventId, emoteWallId: wall.emoteWallId }, auth0Token
      )
      const fold = (prev: EventDetail): EventDetail => withoutStreamWallRow(prev, wall.emoteWallId)
      reapplyStreamWrite(setEvent, fold)
      refresh(fold)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete emote wall')
    } finally {
      setDeletingId(null)
    }
  }

  const streamForm = { event }

  if (view === 'create') {
    return (
      <EmoteWallForm
        existing={null}
        auth0Token={auth0Token}
        overlayLink={null}
        onSaved={(wall) => {
          upsert(wall)
          void navigate(`${basePath}/${wall.emoteWallId}`, { replace: true })
        }}
        onCancel={goList}
        backLabel={backLabel}
        stream={streamForm}
      />
    )
  }

  if (typeof view === 'object') {
    const editing = (event.emoteWalls ?? []).find((w) => w.emoteWallId === view.edit) ?? null
    if (editing == null) {
      return (
        <div className='mt-4 px-4 pb-4'>
          <ErrorInline>That emote wall no longer exists.</ErrorInline>
          <button onClick={goList} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
            {backLabel}
          </button>
        </div>
      )
    }
    return (
      <EmoteWallForm
        existing={editing}
        auth0Token={auth0Token}
        overlayLink={null}
        onSaved={upsert}
        onCancel={goList}
        backLabel={backLabel}
        stream={streamForm}
      />
    )
  }

  let listContent: JSX.Element
  if (rows.length === 0) {
    listContent = (
      <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary' data-testid='stream-wall-empty'>
        <FaceSmileIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
        <p>{STREAM_WALL_COPY.empty}</p>
      </div>
    )
  } else {
    listContent = (
      <ul className='space-y-2' data-testid='stream-emote-walls-list'>
        {rows.map((wall) => (
          <StreamWallListRow
            key={wall.emoteWallId}
            wall={wall}
            onEdit={() => goEdit(wall.emoteWallId)}
            onDelete={() => { void handleDelete(wall) }}
            deleting={deletingId === wall.emoteWallId}
            busy={deletingId != null}
          />
        ))}
      </ul>
    )
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5' data-testid='stream-emote-walls'>
        <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
          <h3 className='text-base font-semibold text-dm-text-primary'>{STREAM_WALL_COPY.heading}</h3>
          <div className='flex flex-wrap items-center gap-2'>
            <button
              type='button'
              onClick={() => setCopyOpen(true)}
              disabled={ended}
              data-testid='stream-wall-copy-button'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              <DocumentDuplicateIcon className='size-3.5' aria-hidden='true' />
              {STREAM_WALL_COPY.copyButton}
            </button>
            <button
              type='button'
              data-testid='stream-wall-new-button'
              onClick={goCreate}
              disabled={ended}
              className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              <PlusIcon className='size-3.5' aria-hidden='true' />
              {STREAM_WALL_COPY.newButton}
            </button>
          </div>
        </div>
        {ended && (
          <p className='mb-3 text-[11px] text-dm-text-accent' data-testid='stream-ended-note'>{STREAM_ENDED_NOTE}</p>
        )}
        {rowError != null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}
        {listContent}
        <p className='mt-4 text-[11px] text-dm-text-accent' data-testid='stream-wall-note'>
          {STREAM_WALL_COPY.note}
        </p>
      </section>
      {copyOpen && (
        <CopyIntoStreamDialog
          kind='wall'
          event={event}
          onCopied={(row) => { upsert(row as EmoteWallSummary) }}
          onClose={() => setCopyOpen(false)}
        />
      )}
    </div>
  )
}
